import Controller from "../class.Controller.js";
import TransporterLog from "../class.TransporterLog.js";
import TransporterAnalyticsPlaythrough from "./class.TransporterAnalyticsPlaythrough.js";
import TransporterAnalyticsMock from "./class.TransporterAnalyticsMock.js";
import TransporterAnalyticsReporter from "./class.TransporterAnalyticsReporter.js";

// eslint-disable-next-line no-unused-vars
const LOGGER = new TransporterLog({
	attributes: {
		name: "TransporterAnalytics",
		color: "lime",
	},
});

const ERROR_THRESHOLD = 3;

export default class TransporterAnalytics extends Controller {
	/**
	 * @param {object} options
	 * @param {object} options.attributes
	 * @param {number} options.attributes.collection_id
	 * @param {number} options.attributes.document_id
	 * @param {number} options.attributes.version
	 * @param {number} options.attributes.user_id
	 * @param {number} [options.attributes.playthrough_seen]
	 * @param {number} [options.attributes.playthrough_id]
	 */
	constructor(options) {
		super(options);
		this.initialize();
	}

	initialize() {
		this.enabled = true;
		this.errorCount = 0;

		// Queue for things we want to ajax.
		this.queue = [];

		// Reference to current playthrough object.
		this.playthrough = null;
		this.forceNewPlaythrough = false;

		// The mock is where we store data to be sent.
		this.mock = new TransporterAnalyticsMock({
			attributes: {
				controller: this,
				seen: this.playthrough_seen,
			},
		});

		// The reporter sends the data.
		this.reporter = new TransporterAnalyticsReporter({
			attributes: {
				controller: this,
				mock: this.mock,
			},
		});

		const {
			collection_id,
			document_id,
			version,
			playthrough_id,
			user_id,
			class_id,
		} = this;

		// Create a playthrough object.
		this.playthrough = new TransporterAnalyticsPlaythrough({
			attributes: {
				controller: this,
				collection_id,
				document_id,
				version,
				user_id,
				class_id,
				playthrough_id: playthrough_id || null,
			},
		});

		this.initialized = true;
		this._processQueue();
	}

	reset(forceNewPlaythrough = false) {
		this.initialized = false;
		this.forceNewPlaythrough = forceNewPlaythrough;
		this.initialize();
	}

	/**
	 * Enqueues an ajax call to be processed.
	 * @param {string} alias
	 * @param {object} request
	 * @returns {Promise}
	 */
	enqueue(alias, request) {
		return new Promise((resolve, reject) => {
			this.queue.push({alias, request, resolve, reject});
			if (this.initialized) {
				this._processQueue();
			}
		});
	}

	getPlaythrough() {
		return this.playthrough;
	}

	isEnabled() {
		return this.enabled;
	}

	setEnabled(val) {
		this.enabled = !!val;
	}

	incrementErrorCount() {
		this.errorCount++;
		if (this.errorCount >= ERROR_THRESHOLD) {
			this.setEnabled(false);
		}
	}

	_run(alias, request) {
		return new Promise((resolve, reject) => {
			if (this.enabled) {
				this.mock.run(alias, request, (success, result) => {
					if (success) {
						resolve(result.response);
					} else {
						reject(result);
					}
				});
			} else {
				reject({
					error_code: "E_TRANSPORTER_ANALYTICS_NOT_ENABLED",
					error_msg: "TransporterAnalytics is not enabled.",
				});
			}
		});
	}

	/**
	 * Processes items currently in the queue.
	 */
	_processQueue() {
		if (!this.queue.length) {
			return;
		}
		let toDo = [];
		if (this.mock.playthroughs.length && this.playthrough.playthrough_id) {
			// If we know the playthrough id, we can proceed as normal.
			toDo = [...this.queue];
			this.queue = [];
		} else {
			// If we don't know the playthrough id, we have to do that first.
			toDo = this.queue.filter(
				(item) => item.alias === TransporterAnalyticsPlaythrough.ALIAS.GET_ID
			);
			if (toDo.length) {
				this.queue.splice(this.queue.indexOf(toDo[0]), 1);
			}
		}
		for (let item of toDo) {
			this._processItem(item);
		}
	}

	async _processItem(item) {
		if (item.alias !== TransporterAnalyticsPlaythrough.ALIAS.GET_ID) {
			item.request.playthrough_id = await this.playthrough.getId();
		}
		if ("id" in item.request && _.isFunction(item.request.id)) {
			try {
				item.request.id = await item.request.id();
			} catch (error) {
				item.reject(error);
			}
		}
		try {
			const result = await this._run(item.alias, item.request);
			item.resolve(result);
		} catch (error) {
			item.reject(error);
		}
	}
}
